import { StreamChat } from 'stream-chat';
import { STREAM_API_KEY as CONFIGURED_KEY } from '../firebase';

// Stream's public client key — tracks the active Firebase project (set in firebase-{test,prod}.js).
// `REACT_APP_STREAM_API_KEY` env var honored as an override for local dev.
const STREAM_API_KEY = CONFIGURED_KEY || process.env.REACT_APP_STREAM_API_KEY || '';

export const streamClient = STREAM_API_KEY ? StreamChat.getInstance(STREAM_API_KEY) : null;

let connectionState = 'disconnected'; // 'disconnected'|'connecting'|'connected'|'error'
let connectedUserId = null;
let connectingPromise = null;
let connectingUserId = null;
const listeners = new Set();

const setState = (next) => {
  connectionState = next;
  listeners.forEach((cb) => cb(next));
};

export const subscribeToStreamConnection = (cb) => {
  listeners.add(cb);
  cb(connectionState);
  return () => listeners.delete(cb);
};

export const getStreamConnectionState = () => connectionState;

// Idempotent: re-calling with same uid while connected is a no-op
export const connectStreamUser = async (user, tokenOrProvider) => {
  if (!streamClient) throw new Error('Stream client not configured (REACT_APP_STREAM_API_KEY missing)');
  if (connectedUserId === user.id && connectionState === 'connected') return streamClient;
  if (connectingPromise && connectingUserId === user.id) return connectingPromise;
  setState('connecting');
  connectingUserId = user.id;
  connectingPromise = (async () => {
    try {
      if (connectedUserId && connectedUserId !== user.id) {
        await streamClient.disconnectUser();
      }
      await streamClient.connectUser(
        { id: user.id, name: user.name, image: user.image },
        tokenOrProvider,
      );
      if (connectingUserId !== user.id) {
        // We were disconnected (or a different user started connecting) while connecting.
        // Drop our connection rather than leaking a connected client for a stale user.
        try { await streamClient.disconnectUser(); } catch (_) { /* ignore */ }
        throw new Error('Stream connect superseded');
      }
      connectedUserId = user.id;
      setState('connected');
      return streamClient;
    } catch (e) {
      if (e?.message !== 'Stream connect superseded') {
        setState('error');
      }
      throw e;
    } finally {
      connectingPromise = null;
      connectingUserId = null;
    }
  })();
  return connectingPromise;
};

export const disconnectStreamUser = async () => {
  if (!streamClient) return;
  if (connectingPromise) {
    try { await connectingPromise; } catch (_) { /* ignore */ }
  }
  if (!connectedUserId) return;
  await streamClient.disconnectUser();
  connectedUserId = null;
  connectingUserId = null;
  setState('disconnected');
};

export const waitForStreamConnection = async (timeoutMs = 15000) => {
  if (connectionState === 'connected') return;
  await new Promise((resolve, reject) => {
    const t = setTimeout(() => {
      unsub();
      reject(new Error('Stream connection timeout'));
    }, timeoutMs);
    const unsub = subscribeToStreamConnection((s) => {
      if (s === 'connected') { clearTimeout(t); unsub(); resolve(); }
      if (s === 'error') { clearTimeout(t); unsub(); reject(new Error('Stream connection error')); }
    });
  });
};
